'use client';

import { motion, useScroll, useTransform } from 'motion/react';
import { useSelectedLayoutSegment } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Drawer } from 'vaul';
import Icon from '@/components/shared/Icon';
import Logo from '@/components/shared/Logo';
import useToggleSmoothScroll from '@/hooks/useToggleSmoothScroll';
import useViewportSize from '@/hooks/useViewportSize';
import cn from '@/lib/cn';
import { INFO_EMAIL_ADDRESS, MARKETPLACE_URLS } from '@/lib/constants';
import { Link } from '@/lib/i18n/navigation';
import BoundedIcon from '../shared/BoundedIcon';

const mainItems = ['company', 'partners', 'career', 'press'] as const;

function MobileMenu() {
  const locale = useLocale();
  const t = useTranslations('navigations');
  const toggle = useToggleSmoothScroll();

  const [isOpen, setIsOpen] = useState(false);

  function onOpenChange(open: boolean) {
    setIsOpen(open);
    toggle(!open);
  }

  function onLinkClick() {
    setIsOpen(false);
    toggle(true);

    document.documentElement.scrollTop = 0;
  }

  return (
    <Drawer.Root
      open={isOpen}
      autoFocus
      onOpenChange={onOpenChange}
    >
      <Drawer.Trigger
        className="flex size-6 items-center justify-center lg:hidden"
        aria-label={t('open-menu')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="10"
          viewBox="0 0 20 10"
        >
          <path d="M0,0h20v2H0V0ZM0,8h20v2H0v-2Z" />
        </svg>
      </Drawer.Trigger>

      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-xs" />
        <Drawer.Content
          className="fixed inset-0 top-auto z-50 outline-none"
          aria-describedby={undefined}
        >
          <Drawer.Title className="sr-only">
            {t('navigation-title')}
          </Drawer.Title>

          <div className="rounded-t-[1.25rem] bg-white px-6 pt-3.5 pb-5">
            <Drawer.Handle className="!w-15" />

            <nav className="mt-10">
              <ul className="space-y-6">
                {mainItems.map((item) => (
                  <li key={item}>
                    <Link
                      className="text-md flex items-center justify-between font-medium"
                      href={`/${item}`}
                      onClick={onLinkClick}
                    >
                      {t(item)}

                      <BoundedIcon
                        background="gray"
                        name="chevron-right"
                        size="sm"
                      />
                    </Link>
                  </li>
                ))}

                <li>
                  <a
                    className="text-md flex items-center justify-between font-medium"
                    href={`mailto:${INFO_EMAIL_ADDRESS}`}
                  >
                    {t('contact')}

                    <BoundedIcon
                      background="gray"
                      name="chevron-right"
                      size="sm"
                    />
                  </a>
                </li>
              </ul>
            </nav>

            <p className="border-neutral-90 mt-6 border-t pt-6">
              <a
                className="text-md flex items-center justify-between font-medium"
                href={MARKETPLACE_URLS[locale]}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('marketplace.label')}

                <BoundedIcon
                  background="gray"
                  name="chrono24"
                  size="sm"
                />
              </a>
            </p>

            <Drawer.Close className="mt-12 w-full rounded-full border border-black/8 p-4 font-semibold">
              {t('close-menu')}
            </Drawer.Close>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export default function Navigation() {
  const locale = useLocale();
  const t = useTranslations('navigations');
  const segment = useSelectedLayoutSegment();

  /* Scroll animation */

  const { scrollY } = useScroll();
  const { breakpoint } = useViewportSize();
  const scrolledBorderRadius = ['xs', 'sm', 'md'].includes(breakpoint)
    ? '0.25rem'
    : '0.375rem';

  const y = useTransform(scrollY, [75, 250], ['1.25rem', '0rem']);
  const borderRadius = useTransform(
    scrollY,
    [75, 250],
    [scrolledBorderRadius, '0rem'],
  );
  const width = useTransform(
    scrollY,
    [75, 250],
    ['calc(100vw - 2.5rem)', 'calc(100vw - 0rem)'],
  );

  /* Render */

  return (
    <motion.header
      className="ring-deep-blue-30/8 text-deep-blue-10 fixed top-0 left-1/2 z-30 flex -translate-x-1/2 justify-between bg-white py-3.5 pr-5.5 pl-6 text-xs font-medium ring-1 lg:px-0 lg:py-0"
      style={{
        borderRadius,
        width,
        y,
      }}
    >
      <div className="flex shrink-0 flex-col justify-center lg:px-7 lg:py-4.5">
        {segment === 'company' ? (
          <Logo className="text-deep-blue-20 h-5 w-29" />
        ) : (
          <Link
            className="outline-offset-4"
            href="/company"
            aria-label={t('logo-link')}
          >
            <Logo className="text-deep-blue-20 h-5 w-29" />
          </Link>
        )}
      </div>

      <nav className="border-deep-blue-30/8 hidden grow flex-col justify-center border-r border-l py-4.5 pr-7 pl-11 lg:flex">
        <ul className="flex gap-6">
          {mainItems.map((item) => (
            <li key={item}>
              <Link
                className={cn(
                  'flex items-center gap-2 transition-colors before:size-1.5 before:bg-current before:opacity-0 before:transition-opacity',
                  segment === item
                    ? 'before:opacity-100'
                    : 'hover:text-deep-blue-10/60',
                )}
                href={`/${item}`}
              >
                {t(item)}
              </Link>
            </li>
          ))}

          <li className="ml-auto pl-14">
            <a
              className="group/link hover:text-deep-blue-10/60 flex items-center gap-2 transition-colors"
              href={`mailto:${INFO_EMAIL_ADDRESS}`}
            >
              {t('contact')}

              <span className="bg-neutral-95 flex size-4.5 items-center justify-center rounded-xs transition group-hover/link:translate-x-0.5">
                <svg
                  className="transition-transform delay-50 group-hover/link:translate-x-0.25"
                  xmlns="http://www.w3.org/2000/svg"
                  width="4"
                  height="7"
                  viewBox="0 0 4 7"
                  fill="currentColor"
                >
                  <path d="M.17.18C.4-.06.77-.06,1,.18l2.82,2.9c.23.24.23.62,0,.85l-2.82,2.9c-.23.24-.6.24-.83,0-.23-.24-.23-.62,0-.85l2.41-2.47L.17,1.03C-.06.79-.06.41.17.18Z" />
                </svg>
              </span>
            </a>
          </li>
        </ul>
      </nav>

      <div className="hidden shrink-0 px-7 py-4.5 lg:block">
        <a
          className="group/link hover:text-deep-blue-10/60 flex items-center gap-2 transition-colors"
          href={MARKETPLACE_URLS[locale]}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('marketplace.label')}

          <span className="bg-neutral-95 flex size-4.5 items-center justify-center rounded-xs transition-transform group-hover/link:translate-x-0.5">
            <Icon
              className="size-3"
              name="chrono24"
            />
          </span>
        </a>
      </div>

      <MobileMenu />
    </motion.header>
  );
}
